'use client';

import React, { useState, useEffect } from 'react';
import { fetchPromotionAlerts, isPromotionValid } from '@/services/api';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

const PopupBanner = ({ page = 'home', disablePromotion = false }) => {
  const router = useRouter();
  const [banner, setBanner] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Skip fetching if disabled or banner was closed in this session
    if (disablePromotion || sessionStorage.getItem('bannerClosed')) {
      return;
    }

    // Track fetch attempts to prevent infinite retries
    let fetchAttempts = 0;
    const MAX_FETCH_ATTEMPTS = 3;
    
    const fetchPromotions = async () => {
      // Skip if already loading or max attempts reached
      if (isLoading || fetchAttempts >= MAX_FETCH_ATTEMPTS) return;
      
      setIsLoading(true);
      fetchAttempts++;
      
      try {
        // Get the selected property code from localStorage
        let propertyCode = null;
        try {
          const selectedProperty = localStorage.getItem('selectedProperty');
          if (selectedProperty) {
            const property = JSON.parse(selectedProperty);
            propertyCode = property.code;
          }
        } catch (error) {
          console.error('Error parsing selected property:', error);
        }

        // Reset banner closed status when property changes
        const lastPropertyCode = sessionStorage.getItem('lastPropertyCode');
        if (propertyCode && lastPropertyCode !== propertyCode) {
          sessionStorage.removeItem('bannerClosed');
          sessionStorage.setItem('lastPropertyCode', propertyCode);
        }

        // Get the last property code used for caching promotions
        const cachedPropertyCode = sessionStorage.getItem('cachedPromotionPropertyCode');
        
        // Clear promotion cache if property code has changed
        if (propertyCode && cachedPropertyCode && cachedPropertyCode !== propertyCode) {
          console.log(`Property code changed from ${cachedPropertyCode} to ${propertyCode}, clearing promotion cache`);
          sessionStorage.removeItem('cachedPromotions');
          sessionStorage.removeItem('cachedPromotionsTimestamp');
          sessionStorage.setItem('cachedPromotionPropertyCode', propertyCode);
        } else if (propertyCode && !cachedPropertyCode) {
          // Store current property code
          sessionStorage.setItem('cachedPromotionPropertyCode', propertyCode);
        }
        
        // Check for cached promotions
        const cachedPromotions = sessionStorage.getItem('cachedPromotions');
        const cachedTimestamp = sessionStorage.getItem('cachedPromotionsTimestamp');
        const now = new Date().getTime();
        const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
        
        // Use cached promotions if available, not expired, and property code matches
        if (cachedPromotions && cachedTimestamp && 
            (now - parseInt(cachedTimestamp)) < CACHE_DURATION &&
            (!propertyCode || !cachedPropertyCode || propertyCode === cachedPropertyCode)) {
          try {
            const parsedPromotions = JSON.parse(cachedPromotions);
            if (Array.isArray(parsedPromotions) && parsedPromotions.length > 0) {
              const validPromotions = parsedPromotions.filter(isPromotionValid);
              if (validPromotions.length === 0) return;
              console.log('Using cached promotions for property:', cachedPropertyCode || 'default');
              setPromotions(validPromotions);
              setCurrentIndex(0);
              setBanner(validPromotions[0]);
              
              // Show the banner after a delay
              setTimeout(() => {
                setIsVisible(true);
              }, 2000);
              
              setIsLoading(false);
              return;
            }
          } catch (cacheError) {
            console.warn('Error parsing cached promotions:', cacheError);
          }
        }

        // Fetch promotions with retry mechanism
        const result = await fetchPromotionAlerts(page, propertyCode);
        
        if (result.status === 'success' && Array.isArray(result.data) && result.data.length > 0) {
          const validPromotions = result.data.filter(isPromotionValid);
          if (validPromotions.length === 0) {
            setIsLoading(false);
            return;
          }

          // Cache the successful result
          try {
            sessionStorage.setItem('cachedPromotions', JSON.stringify(validPromotions));
            sessionStorage.setItem('cachedPromotionsTimestamp', now.toString());
          } catch (cacheError) {
            console.warn('Error caching promotions:', cacheError);
          }

          setPromotions(validPromotions);
          setCurrentIndex(0);
          setBanner(validPromotions[0]);
          
          // Show the banner after a delay
          setTimeout(() => {
            setIsVisible(true);
          }, 2000);
        } else if (result.status === 'error') {
          console.warn('Error fetching promotions:', result.message);
          // Try to use cached promotions even if they're expired
          if (cachedPromotions) {
            try {
              const parsedPromotions = JSON.parse(cachedPromotions);
              const validPromotions = Array.isArray(parsedPromotions) ? parsedPromotions.filter(isPromotionValid) : [];
              if (validPromotions.length > 0) {
                console.log('Using expired cached promotions after fetch error');
                setPromotions(validPromotions);
                setCurrentIndex(0);
                setBanner(validPromotions[0]);
                
                // Show the banner after a delay
                setTimeout(() => {
                  setIsVisible(true);
                }, 2000);
              }
            } catch (cacheError) {
              console.warn('Error parsing expired cached promotions:', cacheError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching promotions:', error);
        // Try to use cached promotions as fallback
        const cachedPromotions = sessionStorage.getItem('cachedPromotions');
        if (cachedPromotions) {
          try {
            const parsedPromotions = JSON.parse(cachedPromotions);
            const validPromotions = Array.isArray(parsedPromotions) ? parsedPromotions.filter(isPromotionValid) : [];
            if (validPromotions.length > 0) {
              console.log('Using cached promotions after error');
              setPromotions(validPromotions);
              setCurrentIndex(0);
              setBanner(validPromotions[0]);
              
              // Show the banner after a delay
              setTimeout(() => {
                setIsVisible(true);
              }, 2000);
            }
          } catch (cacheError) {
            console.warn('Error parsing cached promotions after error:', cacheError);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if not disabled
    if (!disablePromotion) {
      fetchPromotions();
    }

    // Set up rotation if there are multiple promotions
    const rotationInterval = setInterval(() => {
      setPromotions(prevPromotions => {
        if (prevPromotions.length <= 1) return prevPromotions;
        
        setCurrentIndex(prevIndex => {
          const nextIndex = (prevIndex + 1) % prevPromotions.length;
          setBanner(prevPromotions[nextIndex]);
          return nextIndex;
        });
        
        return prevPromotions;
      });
    }, 30000); // Rotate every 30 seconds

    // Set up timer
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => {
      clearInterval(rotationInterval);
      clearInterval(timer);
    };
  }, [page, disablePromotion]);

  const handleClose = () => {
    setIsClosing(true);
    
    // Store in session storage that the banner was closed
    sessionStorage.setItem('bannerClosed', 'true');
    
    // Hide the banner after animation completes
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  const handleBookNow = () => {
    // If the banner has a URL destination, navigate to it
    if (banner.url_destination) {
      router.push(banner.url_destination);
    }
    
    // Close the popup
    handleClose();
  };

  if (!banner || !isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
      <div className={`bg-surface rounded-lg shadow-lg relative overflow-hidden transition-all duration-300 ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`} style={{ maxWidth: '600px', maxHeight: '75vh' }}>
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 shadow-md"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {timeLeft > 0 && (
          <div className="absolute top-3 left-3 bg-primary text-on-primary px-3 py-1 rounded-full text-sm font-medium z-10 shadow-md">
            {timeLeft}s
          </div>
        )}
        
        {banner.url_promo && (
          <div className="h-full overflow-hidden" style={{ maxHeight: '65vh' }}>
            <img 
              src={banner.url_promo} 
              alt="Promotion" 
              className="w-full h-auto object-contain"
              style={{ 
                maxHeight: '65vh',
                cursor: banner.url_destination ? 'pointer' : 'default'
              }}
              onClick={banner.url_destination ? handleBookNow : undefined}
            />
          </div>
        )}
        
        {banner.url_destination && (
          <div className="p-4 bg-gray-50 border-t border-gray-100">
            <button
              onClick={handleBookNow}
              className="w-full py-2 px-4 bg-primary text-on-primary font-medium rounded hover:bg-primary-hover active:bg-primary-active transition-colors duration-200 text-center"
            >
              Book Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PopupBanner;
